// src/services/slaAiApi.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — SLA AI API client (backend /api/v1/sla-ai)
// Every call takes an optional AbortSignal; errors carry .status and the server message.
// ─────────────────────────────────────────────────────────────────────────────
import { config } from '../config';
import { getAuthToken } from '../utils/auth';

// config.apiUrl is …/api/v1 in FlowSense (MailPage calls `${apiUrl}/sent-emails`); tolerate …/api or a bare host
const BASE = (() => {
    const raw = String(config.apiUrl || '').replace(/\/+$/, '');
    if (/\/v1$/.test(raw)) return `${raw}/sla-ai`;
    if (/\/api$/.test(raw)) return `${raw}/v1/sla-ai`;
    return `${raw}/api/v1/sla-ai`;
})();

const qs = (params = {}) => {
    const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 'all');
    return p.length ? `?${new URLSearchParams(p).toString()}` : '';
};

async function request(path, { method = 'GET', body, signal } = {}) {
    const token = getAuthToken();
    let res;
    try {
        res = await fetch(`${BASE}${path}`, {
            method,
            signal,
            headers: {
                Accept: 'application/json',
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        if (e?.name === 'AbortError') throw e;
        const err = new Error('Cannot reach the FlowSense server. Check your connection.');
        err.status = 0;
        throw err;
    }
    let json = {};
    try { json = await res.json(); } catch { json = {}; }
    if (!res.ok || json.success === false) {
        const err = new Error(json.message || (res.status === 403 ? 'You do not have permission for this SLA AI action.' : `SLA AI request failed (${res.status}).`));
        err.status = res.status;
        throw err;
    }
    return json;
}

export const slaAiApi = {
    overview: (signal) => request('/overview', { signal }).then(r => r.data),
    config: (signal) => request('/config', { signal }).then(r => r.data),
    decisions: (filters = {}, signal) => request(`/decisions${qs(filters)}`, { signal }),
    decision: (id, signal) => request(`/decisions/${encodeURIComponent(id)}`, { signal }).then(r => r.data),
    approve: (id, { userId, note } = {}) => request(`/decisions/${encodeURIComponent(id)}/approve`, { method: 'POST', body: { user_id: userId || undefined, note: note || undefined } }),
    reject: (id, { note } = {}) => request(`/decisions/${encodeURIComponent(id)}/reject`, { method: 'POST', body: { note: note || undefined } }),
    revert: (id, { note } = {}) => request(`/decisions/${encodeURIComponent(id)}/revert`, { method: 'POST', body: { note: note || undefined } }),
    scoreboard: (filters = {}, signal) => request(`/scoreboard${qs(filters)}`, { signal }),
    assigneeHistory: (key, days = 90, signal) => request(`/assignees/${encodeURIComponent(key)}/history${qs({ days })}`, { signal }).then(r => r.data),
    preview: (workOrderId, signal) => request(`/work-orders/${encodeURIComponent(workOrderId)}/preview`, { signal }).then(r => r.data),
    grade: (workOrderId) => request(`/work-orders/${encodeURIComponent(workOrderId)}/grade`, { method: 'POST', body: {} }).then(r => r.data),
    escalate: (workOrderId, { reassign = false } = {}) => request(`/work-orders/${encodeURIComponent(workOrderId)}/escalate`, { method: 'POST', body: { reassign } }),
    runNow: () => request('/run', { method: 'POST', body: {} }).then(r => r.data),
    notifications: ({ unread = false, limit = 30 } = {}, signal) => request(`/notifications${qs({ unread: unread ? 'true' : undefined, limit })}`, { signal }),
    markRead: (id = 'all') => request(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', body: {} }),
};

export default slaAiApi;
